/**
 * Typed mirror of the backend's per-recipe shape_floor v1.2 payload
 * (template_content_crew.recipe_adapter.shape_floor_payload), plus the
 * display-layer derivations the anatomy surfaces (S2 Shape, S3 cards,
 * S4 detail pane) consume.
 *
 * Hard rules (ratified record):
 * - This module NEVER re-derives planner or floor logic. It types and parses
 *   what the adapter serves; all math lives backend-side.
 * - The sections unit bridge is adapter-owned: the payload's
 *   `body_section_floor` is already in config (body-section) units — consumers
 *   do plain max(), zero arithmetic, zero unit knowledge.
 * - advisory_* fields are typed for transport completeness but are NEVER
 *   rendered as floors (v1.1 contract, binding).
 * - observed_shape (A2) is NOT served by the backend yet (verified
 *   2026-07-23: no payload field). Forward-compatible type + n>=5 gate only —
 *   never fabricated, never rendered until the field exists.
 */

// ---------------------------------------------------------------------------
// Payload mirror
// ---------------------------------------------------------------------------

/** Verbatim mirror of asdict(ArticleShapeRules) as served under `rules`. */
export interface ShapeFloorRules {
  // Hard floors
  min_words: number;
  min_top_nodes: number;
  min_headings: number;
  min_h2: number;
  min_h3: number;
  min_callouts: number;
  min_faq_items: number;
  require_toc: boolean;
  require_key_takeaways: boolean;
  require_body_callout: boolean;
  require_list: boolean;
  require_mid_article_table: boolean;
  require_cta_before_faq: boolean;
  require_image_scaffold: boolean;
  min_image_scaffolds: number;
  table_position_free: boolean;
  require_title_coverage: boolean;
  // Advisory (demoted aspirational targets). Typed for transport; NEVER
  // consumed for display floors.
  advisory_min_top_nodes?: number;
  advisory_min_headings?: number;
  advisory_min_h3?: number;
  advisory_require_cta_before_faq?: boolean;
  advisory_min_words?: number | null;
}

/** The profiler branch ids the backend can serve (article_shape.py). */
export type ProfileClass =
  | 'short_form'
  | 'listicle'
  | 'how_to'
  | 'comparison'
  | 'informational'
  | 'fallback';

/** The v1.2 per-recipe envelope (+ S1/S3 display fields). */
export interface ShapeFloorPayload {
  rules: ShapeFloorRules;
  floor_version: number;
  source: string;
  backend_commit: string;
  structural_rules: string[];
  /** Sections-axis floor in BODY-section units (adapter-owned bridge). */
  body_section_floor: number;
  /** Profiler branch id (article_shape.py single owner; S3 gallery grouping). */
  profile_class: ProfileClass;
}

/**
 * A2 observed-shape annotation — forward-compatible type only.
 * The backend aggregate is explicitly deferred out of the anatomy PR;
 * nothing renders until this field exists on the wire AND passes the gate.
 */
export interface ObservedShape {
  /** Completed-article sample size behind the medians. */
  article_count: number;
  /** Explicit window label, e.g. "last 5 weeks, 263 articles". */
  window_label: string;
  median_sections?: number;
  median_words?: number;
}

// ---------------------------------------------------------------------------
// Runtime parsing (transport-resilient; bad payloads degrade, never throw)
// ---------------------------------------------------------------------------

const REQUIRED_RULE_KEYS: ReadonlyArray<keyof ShapeFloorRules> = [
  'min_words',
  'min_h2',
  'min_h3',
  'min_callouts',
  'min_faq_items',
  'require_toc',
  'require_key_takeaways',
  'require_body_callout',
  'require_list',
  'require_mid_article_table',
  'require_image_scaffold',
  'min_image_scaffolds',
];

/**
 * Parse an untrusted `shape_floor` value into the typed payload.
 * Returns undefined for anything that is not a v2 payload with a complete
 * rules object — callers then render config-only (accepted safe degradation,
 * contract item 7: previews under-promise rather than over-promise).
 */
export function parseShapeFloor(raw: unknown): ShapeFloorPayload | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const candidate = raw as Record<string, unknown>;
  if (candidate.floor_version !== 2) return undefined;
  const rules = candidate.rules;
  if (!rules || typeof rules !== 'object' || Array.isArray(rules)) return undefined;
  const rulesRec = rules as Record<string, unknown>;
  for (const key of REQUIRED_RULE_KEYS) {
    if (!(key in rulesRec)) return undefined;
  }
  if (typeof candidate.body_section_floor !== 'number') return undefined;
  if (!Array.isArray(candidate.structural_rules)) return undefined;
  if (typeof candidate.profile_class !== 'string') return undefined;
  return candidate as unknown as ShapeFloorPayload;
}

// ---------------------------------------------------------------------------
// Glyph-state derivation (fixed-slot baseline row source for S2/S4)
// ---------------------------------------------------------------------------

/** Baseline components with a fixed slot on every Shape / detail pane. */
export type GlyphSlot =
  | 'toc'
  | 'keyTakeaways'
  | 'list'
  | 'table'
  | 'callout'
  | 'image'
  | 'faq';

/**
 * Floor-required presence per baseline slot, derived ONLY from the floor's
 * require_* booleans. FAQ is always present (R7: forced True backend-side).
 * Effective display presence may additionally be forced ON by recipe config —
 * that combination happens in clampToFloor.ts (display layer); this function
 * is the pure floor half.
 */
export function glyphStatesFromFloor(
  rules: ShapeFloorRules,
): Record<GlyphSlot, boolean> {
  return {
    toc: rules.require_toc,
    keyTakeaways: rules.require_key_takeaways,
    list: rules.require_list,
    table: rules.require_mid_article_table,
    callout: rules.require_body_callout || rules.min_callouts > 0,
    image: rules.require_image_scaffold || rules.min_image_scaffolds > 0,
    faq: true, // R7 — always-on, never a toggle
  };
}

// ---------------------------------------------------------------------------
// A2 observed-shape gate
// ---------------------------------------------------------------------------

/** A2 display gate: observed annotations render only at n>=5 with a window. */
export function canShowObserved(
  observed: ObservedShape | null | undefined,
): observed is ObservedShape {
  return (
    !!observed &&
    typeof observed.article_count === 'number' &&
    observed.article_count >= 5 &&
    typeof observed.window_label === 'string' &&
    observed.window_label.length > 0
  );
}
